import React from 'react'
import { Car, X } from 'lucide-react'
import { formatCurrency } from '@/utils/formatters'
import { type VehicleIdentityData } from './vehicleIdentity'

interface VehicleIdentityCardProps {
  vehicle: VehicleIdentityData
  variant?: 'plain' | 'selected'
  onRemove?: () => void
  removeDisabled?: boolean
  removeTitle?: string
}

const readModelName = (vehicle: VehicleIdentityData): string => {
  if (typeof vehicle.basic?.model === 'string') return vehicle.basic.model
  if (vehicle.basic?.model && typeof vehicle.basic.model === 'object') return vehicle.basic.model.name || ''
  if (typeof vehicle.model === 'string') return vehicle.model
  return vehicle.model?.name || vehicle.modelName || vehicle.catalog?.model?.name || ''
}

const readBrandName = (vehicle: VehicleIdentityData): string => {
  if (typeof vehicle.basic?.brand === 'string') return vehicle.basic.brand
  if (vehicle.basic?.brand && typeof vehicle.basic.brand === 'object') return vehicle.basic.brand.name || ''
  if (typeof vehicle.brand === 'string') return vehicle.brand
  return vehicle.brand?.name || vehicle.catalog?.brand?.name || vehicle.brandName || vehicle.basic?.brandName || ''
}

const readYear = (vehicle: VehicleIdentityData): number | undefined => {
  return vehicle.year || vehicle.modelYear || vehicle.catalog?.modelYear || vehicle.basic?.year || vehicle.basic?.modelYear
}

export const getVehicleDisplayName = (vehicle: VehicleIdentityData): string => {
  const directName = vehicle.name || vehicle.basic?.name
  if (directName && directName.trim().length > 0) return directName.trim()

  const brand = readBrandName(vehicle)
  const model = readModelName(vehicle)
  const year = readYear(vehicle)
  const composedName = `${brand} ${model} ${year ?? ''}`.trim()
  if (composedName.length > 0) return composedName

  return vehicle.identification?.plate || vehicle.plate || vehicle.id
}

const readPrice = (vehicle: VehicleIdentityData): number => {
  const parsePriceLike = (value: unknown): number | null => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    if (typeof value === 'string') {
      const parsed = parseFloat(value)
      return Number.isFinite(parsed) ? parsed : null
    }
    if (value && typeof value === 'object') {
      const obj = value as { amount?: unknown; value?: unknown }
      return parsePriceLike(obj.amount) ?? parsePriceLike(obj.value)
    }
    return null
  }

  const finalPrice = vehicle.pricing?.sale?.finalPrice
  const parsedFinalPrice = parsePriceLike(finalPrice)
  if (parsedFinalPrice !== null) return parsedFinalPrice

  const announcedPrice = vehicle.pricing?.sale?.announcedPrice
  const parsedAnnouncedPrice = parsePriceLike(announcedPrice)
  if (parsedAnnouncedPrice !== null) return parsedAnnouncedPrice

  const pricingSalePrice = vehicle.pricing?.sale?.salePrice
  const parsedPricingSalePrice = parsePriceLike(pricingSalePrice)
  if (parsedPricingSalePrice !== null) return parsedPricingSalePrice

  if (vehicle.salePrice !== undefined && vehicle.salePrice !== null) {
    const parsedSalePrice = parsePriceLike(vehicle.salePrice)
    if (parsedSalePrice !== null) return parsedSalePrice
  }
  if (vehicle.basic?.salePrice !== undefined && vehicle.basic.salePrice !== null) {
    const parsedBasicSalePrice = parsePriceLike(vehicle.basic.salePrice)
    if (parsedBasicSalePrice !== null) return parsedBasicSalePrice
  }
  if (vehicle.price !== undefined && vehicle.price !== null) {
    const parsedPrice = parsePriceLike(vehicle.price)
    if (parsedPrice !== null) return parsedPrice
  }
  if (vehicle.basic?.price !== undefined && vehicle.basic.price !== null) {
    const parsedBasicPrice = parsePriceLike(vehicle.basic.price)
    if (parsedBasicPrice !== null) return parsedBasicPrice
  }
  return 0
}

const readMileage = (vehicle: VehicleIdentityData): number | null => {
  if (typeof vehicle.mileage === 'number') return vehicle.mileage
  if (typeof vehicle.basic?.mileage === 'number') return vehicle.basic.mileage
  if (typeof vehicle.specifications?.mileage === 'number') return vehicle.specifications.mileage
  return null
}

export const VehicleIdentityCard: React.FC<VehicleIdentityCardProps> = ({
  vehicle,
  variant = 'plain',
  onRemove,
  removeDisabled = false,
  removeTitle = 'Remove vehicle',
}) => {
  const imageUrl =
    vehicle.photos?.[0]?.url ||
    vehicle.images?.[0] ||
    vehicle.basic?.photoUrl ||
    vehicle.basic?.imageUrl
  const priceLabel = formatCurrency(readPrice(vehicle))
  const mileageValue = readMileage(vehicle)
  const mileageLabel = mileageValue === null ? null : `${mileageValue.toLocaleString('pt-BR')} km`

  const variantClassName =
    variant === 'selected' ? 'p-4 bg-surface rounded-lg border border-border' : ''

  return (
    <div className={`flex items-start gap-3 ${variantClassName}`}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={getVehicleDisplayName(vehicle)}
          className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
        />
      ) : (
        <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
          <Car className="h-6 w-6 text-muted-foreground" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Car className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <p className="font-medium text-foreground truncate">{getVehicleDisplayName(vehicle)}</p>
        </div>
        <p className="text-sm text-muted-foreground">{priceLabel}</p>
        {mileageLabel && <p className="text-xs text-muted-foreground">{mileageLabel}</p>}
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={removeDisabled}
          className="hover:bg-muted rounded p-1"
          title={removeTitle}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

